import React, { useEffect, useState, Component } from 'react';
import { Hero } from '../components/Hero';
import { ProblemSection } from '../components/ProblemSection';
import { CoverageSection } from '../components/CoverageSection';
import { HowItWorks } from '../components/HowItWorks';
import { SolutionSection } from '../components/SolutionSection';
import { AudienceSection } from '../components/AudienceSection';
import { FutureSection } from '../components/FutureSection';
import { TrustSection } from '../components/TrustSection';
import { CTASection } from '../components/CTASection';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { motion, useScroll, useSpring, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
// Reusable Watchdog Logo Component
export const WatchdogLogo = ({
  className = 'w-8 h-8'
}: {
  className?: string;
}) => <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-374">
    <path d="M16 2L4 7V14C4 21.5 9.5 28.5 16 30C22.5 28.5 28 21.5 28 14V7L16 2Z" className="fill-indigo-600" data-id="element-375" />
    <path d="M16 8C13.5 8 11.5 10 11.5 12.5C11.5 15 13.5 17 16 17C18.5 17 20.5 15 20.5 12.5C20.5 10 18.5 8 16 8ZM16 15C14.6 15 13.5 13.9 13.5 12.5C13.5 11.1 14.6 10 16 10C17.4 10 18.5 11.1 18.5 12.5C18.5 13.9 17.4 15 16 15Z" className="fill-white" data-id="element-376" />
    <path d="M16 19C12.5 19 9.5 20.5 8 22.5C9.5 25.5 12.5 27 16 27C19.5 27 22.5 25.5 24 22.5C22.5 20.5 19.5 19 16 19Z" className="fill-white opacity-50" data-id="element-377" />
  </svg>;
export function LandingPage() {
  const {
    scrollYProgress
  } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-indigo-100 selection:text-indigo-900 overflow-x-hidden" data-id="element-378">
      {/* Scroll Progress Bar */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-indigo-600 origin-left z-[100]" style={{
      scaleX
    }} data-id="element-379" />

      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200/50 shadow-sm' : 'bg-transparent'}`} data-id="element-380">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-381">
          <div className="flex justify-between h-16 items-center" data-id="element-382">
            {/* Logo */}
            <div className="flex items-center space-x-2 cursor-pointer group z-50" data-id="element-383">
              <WatchdogLogo className="w-8 h-8 group-hover:scale-110 transition-transform duration-300" data-id="element-384" />
              <span className="text-xl font-bold text-slate-900 tracking-tight" data-id="element-385">
                Watchdog
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8" data-id="element-386">
              {['Features', 'How it Works', 'Pricing'].map(item => <a key={item} href="#" className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors" data-id="element-387">
                  {item}
                </a>)}
              <Button variant="primary" size="sm" data-id="element-388">
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center z-50" data-id="element-389">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-slate-600 hover:text-indigo-600 transition-colors" aria-label="Toggle menu" data-id="element-390">
                {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-391" /> : <Menu className="w-6 h-6" data-id="element-392" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence data-id="element-393">
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="md:hidden bg-white border-b border-slate-200 overflow-hidden" data-id="element-394">
              <div className="px-4 pt-4 pb-8 space-y-4 flex flex-col" data-id="element-395">
                {['Features', 'How it Works', 'Pricing'].map(item => <a key={item} href="#" className="text-lg font-medium text-slate-600 hover:text-indigo-600 py-2 border-b border-slate-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-396">
                    {item}
                  </a>)}
                <div className="pt-4" data-id="element-397">
                  <Button variant="primary" size="lg" className="w-full justify-center" data-id="element-398">
                    Get Started
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>

      <main data-id="element-399">
        <Hero data-id="element-400" />
        <CoverageSection data-id="element-401" />
        <ProblemSection data-id="element-402" />
        <HowItWorks data-id="element-403" />
        <SolutionSection data-id="element-404" />
        <TrustSection data-id="element-405" />
        <AudienceSection data-id="element-406" />
        <FutureSection data-id="element-407" />
        <CTASection data-id="element-408" />
      </main>

      <Footer data-id="element-409" />
    </div>;
}